import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users, Search, Plus, Trash2, Pencil, Send, Download, X, Check,
  Mail, Phone, Eye, Calendar, Ship, ArrowRight, ArrowLeft, Percent, Clock, Anchor
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = 'https://static.wixstatic.com/media/ce6ce7_a82e3e86741143d6ab7acd99c121af7b~mv2.png/v1/fill/w_400,h_200,al_c,q_85,usm_0.66_1.00_0.01,enc_auto,quality_auto/croisieres%20catamaran%20corse%20sognudimare.png';
// Lagoon hero photos (served from /public/images on the same site)
const LAGOON_HERO = '/images/hero_lagoon38_sailing.jpg';
const PHOTOS_EXT = [
  '/images/hero_lagoon38_aerial.jpg',
  '/images/hero_lagoon43_paddle.jpg',
  '/images/hero_lagoon43_aerial.jpg',
];
const PHOTOS_INT = [
  '/images/hero_lagoon43_cockpit.jpg',
  '/images/hero_lagoon43_salon.jpg',
  '/images/hero_lagoon43_cabine.jpg',
];

// Same destination photos as on the site DestinationsPage (preview mirror)
const DEST_PHOTOS = {
  'tour-de-corse-croisiere-catamaran': 'https://static.wixstatic.com/media/ce6ce7_0ce032b8fe2e4bf58652c8d18e897478~mv2.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO%20copie.jpg',
  'croisiere-catamaran-corse-du-sud': 'https://static.wixstatic.com/media/ce6ce7_98fe44ee0ba0419794505afe2c70aa95~mv2.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_auto/SOGNUDIMARE%20CROISIERE%20CATAMARAN%20DEPUIS%20AJACCIO.jpg',
  'croisiere-catamaran-ouest-corse': 'https://static.wixstatic.com/media/ce6ce7_f3bee630d0c549388bbc71c0e58fb9ea~mv2.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_auto/sognudimare%20a%20scandola_edited.jpg',
  'croisiere-catamaran-sardaigne-corse': 'https://static.wixstatic.com/media/ce6ce7_9e15d4c9779b4d48ad56a689ab2fe02b~mv2.avif/v1/fill/w_800,h_500,al_c,q_85,enc_auto/LAGOON%2043%20SOGNUDIMARE.avif',
  'croisiere-catamaran-grece-authentique': 'https://static.wixstatic.com/media/ce6ce7_227bd245ee9c48eca3a0f527080bf3a7~mv2.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_auto/cephalonie%20grece.jpg',
  'croisiere-catamaran-grenadines-caraibes': '/images/hero_lagoon43_paddle.jpg',
};
const DEFAULT_DEST_PHOTO = '/images/hero_lagoon38_sailing.jpg';

const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

const fmtDateLong = (d) => {
  if (!d) return '';
  const months = ['','janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  const p = d.split('-');
  if (p.length !== 3) return d;
  const day = parseInt(p[2]);
  return `${day === 1 ? '1er' : day} ${months[parseInt(p[1])]} ${p[0]}`;
};

const tagColors = {
  booking: 'bg-emerald-100 text-emerald-800',
  devis: 'bg-blue-100 text-blue-800',
  prospect: 'bg-amber-100 text-amber-800',
  vip: 'bg-purple-100 text-purple-800',
};

const AdminClients = () => {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingClient, setEditingClient] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [departures, setDepartures] = useState([]);
  const [selectedDepartureIds, setSelectedDepartureIds] = useState(new Set());
  const [customMessage, setCustomMessage] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [offerExpires, setOfferExpires] = useState('');
  const [sending, setSending] = useState(false);
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (token) { fetchClients(); fetchHistory(); } }, [token]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Erreur chargement clients'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients/email-history`, { headers });
      setEmailHistory(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/admin/clients/import`, {}, { headers });
      toast.success(`${res.data.imported} client(s) importé(s)`);
      fetchClients();
    } catch { toast.error("Erreur lors de l'import"); }
    finally { setImporting(false); }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { toast.error('Nom et email requis'); return; }
    try {
      if (editingClient) {
        await axios.put(`${API}/admin/clients/${editingClient.id}`, formData, { headers });
        toast.success('Client modifié');
      } else {
        await axios.post(`${API}/admin/clients`, formData, { headers });
        toast.success('Client ajouté');
      }
      setEditingClient(null); setShowAddForm(false);
      setFormData({ name: '', email: '', phone: '', address: '', notes: '' });
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.error || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/clients/${id}`, { headers });
      toast.success('Client supprimé'); setConfirmDelete(null);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchClients();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({ name: client.name, email: client.email, phone: client.phone || '', address: client.address || '', notes: client.notes || '' });
    setShowAddForm(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const filtered = useMemo(() => {
    const list = clients || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(c => (c.name||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q) || (c.phone||'').includes(q) || (c.tags||[]).some(t => t.toLowerCase().includes(q)));
  }, [clients, search]);

  const toggleAll = () => {
    const f = filtered || [];
    if (selectedIds.size === f.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(f.map(c => c.id)));
  };

  const selectedClients = useMemo(() => clients.filter(c => selectedIds.has(c.id)), [clients, selectedIds]);
  const selectedDepartures = useMemo(() => departures.filter(d => selectedDepartureIds.has(d.id)), [departures, selectedDepartureIds]);

  // Unique inclusions + exclusions from selected departures
  const allIncluded = useMemo(() => {
    const seen = new Set();
    const items = [];
    selectedDepartures.forEach(d => (d.included || []).forEach(item => { if (!seen.has(item)) { seen.add(item); items.push(item); } }));
    return items;
  }, [selectedDepartures]);

  const allNotIncluded = useMemo(() => {
    const seen = new Set();
    const items = [];
    selectedDepartures.forEach(d => (d.not_included || []).forEach(item => { if (!seen.has(item)) { seen.add(item); items.push(item); } }));
    // Always add caisse de bord
    const cdb = "Caisse de bord : 240 € par passager et par semaine";
    if (!seen.has(cdb)) items.push(cdb);
    return items;
  }, [selectedDepartures]);

  const openSendModal = async () => {
    if (selectedIds.size === 0) { toast.error('Sélectionnez au moins un client'); return; }
    try {
      const res = await axios.get(`${API}/admin/clients/upcoming-departures`, { headers });
      setDepartures(res.data);
      setSelectedDepartureIds(new Set());
      setCustomMessage(''); setDiscountPercent(0); setOfferExpires('');
      setModalStep(1); setShowSendModal(true);
    } catch { toast.error('Erreur chargement des départs'); }
  };

  const toggleDeparture = (id) => {
    setSelectedDepartureIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToPreview = () => {
    if (selectedDepartureIds.size === 0) { toast.error('Sélectionnez au moins un départ'); return; }
    setModalStep(2);
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/clients/send-departures`, {
        client_ids: Array.from(selectedIds),
        departure_ids: Array.from(selectedDepartureIds),
        custom_message: customMessage,
        discount_percent: discountPercent,
        offer_expires: offerExpires,
      }, { headers });
      toast.success(res.data.message);
      setShowSendModal(false); setSelectedIds(new Set());
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.error || "Erreur d'envoi"); }
    finally { setSending(false); }
  };

  // Live countdown
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!offerExpires || discountPercent <= 0) { setCountdown(''); return; }
    const update = () => {
      const now = new Date();
      const exp = new Date(offerExpires + 'T23:59:59');
      const diff = exp - now;
      if (diff <= 0) { setCountdown('Offre expirée'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${d}j ${String(h).padStart(2,'0')}h ${String(m).padStart(2,'0')}min ${String(s).padStart(2,'0')}s`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [offerExpires, discountPercent]);

  if (loading) return <AdminLayout title="Clients"><div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-slate-200 rounded-lg animate-pulse" />)}</div></AdminLayout>;

  return (
    <AdminLayout title="Clients">
      <div className="space-y-4" data-testid="admin-clients">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { val: (clients||[]).length, label: 'Total clients', color: 'text-[#0A2540]' },
            { val: (clients||[]).filter(c => (c.tags||[]).includes('booking')).length, label: 'Avec réservation', color: 'text-emerald-600' },
            { val: (clients||[]).filter(c => (c.tags||[]).includes('devis')).length, label: 'Avec devis', color: 'text-blue-600' },
            { val: (clients||[]).filter(c => (c.tags||[]).includes('prospect')).length, label: 'Prospects', color: 'text-amber-600' },
          ].map((s, i) => (
            <Card key={i}><CardContent className="p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </CardContent></Card>
          ))}
        </div>

        {/* Actions bar */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="client-search" placeholder="Rechercher (nom, email, téléphone...)" value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Button data-testid="add-client-btn" onClick={() => { setEditingClient(null); setFormData({ name:'',email:'',phone:'',address:'',notes:'' }); setShowAddForm(true); }} className="bg-[#0A2540] hover:bg-[#0A2540]/90"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
          <Button data-testid="import-clients-btn" onClick={handleImport} variant="outline" disabled={importing}><Download className="h-4 w-4 mr-1" /> {importing ? 'Import...' : 'Importer'}</Button>
          {selectedIds.size > 0 && (
            <Button data-testid="send-departures-btn" onClick={openSendModal} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"><Send className="h-4 w-4 mr-1" /> Envoyer départs ({selectedIds.size})</Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="border-[#D4AF37]"><CardContent className="p-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-[#0A2540]">{editingClient ? 'Modifier le client' : 'Nouveau client'}</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowAddForm(false); setEditingClient(null); }}><X className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="client-name-input" placeholder="Nom complet *" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              <Input data-testid="client-email-input" placeholder="Email *" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <Input data-testid="client-phone-input" placeholder="Téléphone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
              <Input data-testid="client-address-input" placeholder="Adresse" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
            </div>
            <Input data-testid="client-notes-input" placeholder="Notes" value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowAddForm(false); setEditingClient(null); }}>Annuler</Button>
              <Button data-testid="save-client-btn" onClick={handleSave} className="bg-[#0A2540]"><Check className="h-4 w-4 mr-1" /> {editingClient ? 'Modifier' : 'Ajouter'}</Button>
            </div>
          </CardContent></Card>
        )}

        {/* Client list */}
        <Card><CardContent className="p-0">
          <div className="grid grid-cols-[40px_1fr_1fr_120px_100px] gap-2 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase hidden sm:grid">
            <div className="flex items-center justify-center"><Checkbox checked={(filtered||[]).length > 0 && selectedIds.size === (filtered||[]).length} onCheckedChange={toggleAll} data-testid="select-all-clients" /></div>
            <div>Client</div><div>Contact</div><div>Tags</div><div className="text-right">Actions</div>
          </div>
          {(filtered||[]).length === 0 ? (
            <div className="p-8 text-center text-slate-400"><Users className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>{(clients||[]).length === 0 ? "Cliquez sur 'Importer' pour récupérer vos clients." : 'Aucun résultat.'}</p></div>
          ) : (filtered||[]).map(client => (
            <div key={client.id} data-testid={`client-row-${client.id}`} className={`grid grid-cols-1 sm:grid-cols-[40px_1fr_1fr_120px_100px] gap-2 p-3 border-b last:border-0 items-center hover:bg-slate-50 transition-colors ${selectedIds.has(client.id) ? 'bg-blue-50/50' : ''}`}>
              <div className="flex items-center justify-center"><Checkbox checked={selectedIds.has(client.id)} onCheckedChange={() => toggleSelect(client.id)} /></div>
              <div><div className="font-semibold text-[#0A2540] text-sm">{client.name}</div>{client.notes && <div className="text-xs text-slate-400 truncate max-w-[250px]">{client.notes}</div>}</div>
              <div className="space-y-0.5"><div className="flex items-center gap-1 text-xs text-slate-600"><Mail className="h-3 w-3" /> {client.email}</div>{client.phone && <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="h-3 w-3" /> {client.phone}</div>}</div>
              <div className="flex flex-wrap gap-1">{(client.tags||[]).map(tag => <span key={tag} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${tagColors[tag] || 'bg-slate-100 text-slate-600'}`}>{tag}</span>)}</div>
              <div className="flex gap-1 justify-end">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(client)}><Pencil className="h-3.5 w-3.5" /></Button>
                {confirmDelete === client.id ? (
                  <div className="flex gap-1"><Button variant="destructive" size="sm" onClick={() => handleDelete(client.id)}><Check className="h-3.5 w-3.5" /></Button><Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)}><X className="h-3.5 w-3.5" /></Button></div>
                ) : (<Button variant="ghost" size="sm" onClick={() => setConfirmDelete(client.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>)}
              </div>
            </div>
          ))}
        </CardContent></Card>

        {/* ===== HISTORIQUE DES ENVOIS ===== */}
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-slate-50 flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#D4AF37]" />
              <h3 className="font-bold text-[#0A2540] text-sm">Historique des envois</h3>
              <Badge variant="outline" className="ml-auto text-xs">{(emailHistory||[]).length} envoi(s)</Badge>
              {(emailHistory||[]).length > 0 && (
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs ml-1" onClick={async () => {
                  if (!window.confirm('Supprimer tout l\'historique ?')) return;
                  try { await axios.delete(`${API}/admin/clients/email-history`, { headers }); toast.success('Historique vidé'); fetchHistory(); } catch { toast.error('Erreur'); }
                }} data-testid="clear-history-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Tout vider</Button>
              )}
            </div>
            {(emailHistory||[]).length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                <Send className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Aucun envoi pour le moment</p>
                <p className="text-xs mt-1">Sélectionnez des clients et envoyez-leur les prochains départs</p>
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {(emailHistory||[]).map(h => {
                  const sentDate = new Date(h.sent_at);
                  const sentStr = sentDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  const hasDiscount = h.discount_percent > 0;
                  return (
                    <div key={h.id} className="p-3 hover:bg-slate-50 transition-colors group">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Check className="h-4 w-4 text-emerald-500" />
                            <span className="text-sm font-semibold text-[#0A2540]">{sentStr}</span>
                            {hasDiscount && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C4724F]/10 text-[#C4724F] font-bold">-{h.discount_percent}%</span>}
                            <span className="text-xs text-slate-400">→ {h.client_count} client(s)</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Mail className="h-3 w-3 text-blue-400" />
                            {(h.client_names || []).map((n, i) => <span key={i} className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md font-medium">{n}</span>)}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Ship className="h-3 w-3 text-slate-400" />
                            {(h.departures_summary || []).map((d, i) => (
                              <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">{d.cruise_name} ({fmtDate(d.start_date)})</span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                          {h.offer_expires && (
                            <div className="text-[11px] text-slate-500">
                              Fin offre : <span className="text-[#C4724F] font-bold">{fmtDate(h.offer_expires)}</span>
                            </div>
                          )}
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={async () => {
                            try { await axios.delete(`${API}/admin/clients/email-history/${h.id}`, { headers }); fetchHistory(); } catch { toast.error('Erreur'); }
                          }}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ===== SEND MODAL ===== */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowSendModal(false)}>
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="send-departures-modal">

              {/* Header */}
              <div className="p-4 border-b bg-[#0A2540] rounded-t-xl flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-white font-bold text-lg">{modalStep === 1 ? 'Composer votre email' : "Prévisualisation avant envoi"}</h2>
                    <p className="text-white/60 text-sm">{selectedClients.length} destinataire(s)</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowSendModal(false)} className="text-white/70 hover:text-white hover:bg-white/10"><X className="h-5 w-5" /></Button>
                </div>
                <div className="flex gap-2 mt-3">
                  <div className={`flex-1 h-1.5 rounded-full transition-all ${modalStep >= 1 ? 'bg-[#D4AF37]' : 'bg-white/20'}`} />
                  <div className={`flex-1 h-1.5 rounded-full transition-all ${modalStep >= 2 ? 'bg-[#D4AF37]' : 'bg-white/20'}`} />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {modalStep === 1 ? (
                  <div className="p-5 space-y-5">
                    {/* Message */}
                    <div>
                      <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Message personnalisé</label>
                      <textarea data-testid="custom-message-input" className="w-full border rounded-lg p-3 text-sm min-h-[60px] focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none" placeholder="Ex: Cher client, nous avons le plaisir de vous proposer..." value={customMessage} onChange={e => setCustomMessage(e.target.value)} />
                    </div>

                    {/* Discount + Expiry */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><Percent className="h-3.5 w-3.5 text-[#C4724F]" /> Remise (%)</label>
                        <Input data-testid="discount-input" type="number" min="0" max="50" placeholder="0" value={discountPercent || ''} onChange={e => setDiscountPercent(Math.min(50, Math.max(0, parseInt(e.target.value) || 0)))} />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#C4724F]" /> Date d'expiration de l'offre</label>
                        <Input data-testid="expiry-input" type="date" value={offerExpires} onChange={e => setOfferExpires(e.target.value)} />
                      </div>
                    </div>
                    {discountPercent > 0 && (
                      <div className="bg-[#C4724F]/10 border border-[#C4724F]/30 rounded-lg p-3 flex items-center gap-2">
                        <Percent className="h-4 w-4 text-[#C4724F]" />
                        <span className="text-sm text-[#C4724F] font-medium">Remise de {discountPercent}% appliquée sur les tarifs{offerExpires ? ` — Valable jusqu'au ${fmtDate(offerExpires)}` : ''}</span>
                      </div>
                    )}

                    {/* Departures */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-semibold text-slate-700">Cochez les départs à inclure</label>
                        <Badge variant="outline">{selectedDepartureIds.size} sélectionné(s)</Badge>
                      </div>
                      <div className="space-y-1.5 max-h-[35vh] overflow-y-auto pr-1">
                        {departures.map(dep => {
                          const sel = selectedDepartureIds.has(dep.id);
                          const avail = dep.available_cabins;
                          return (
                            <div key={dep.id} data-testid={`departure-${dep.id}`} onClick={() => toggleDeparture(dep.id)}
                              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${sel ? 'border-[#D4AF37] bg-[#D4AF37]/5 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                              <Checkbox checked={sel} className="pointer-events-none" />
                              <Ship className={`h-4 w-4 flex-shrink-0 ${sel ? 'text-[#D4AF37]' : 'text-slate-400'}`} />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-[#0A2540]">{dep.cruise_name}</div>
                                <div className="text-xs text-slate-500">{dep.destination} — {dep.departure_port}</div>
                              </div>
                              <div className="text-right flex-shrink-0 mr-1">
                                <div className="text-sm font-bold text-[#0A2540]">{fmtDate(dep.start_date)}</div>
                                <div className="text-[10px] text-slate-400">au {fmtDate(dep.end_date)}</div>
                              </div>
                              <div className="flex-shrink-0 w-[85px] text-right">
                                {avail <= 0 ? <span className="text-[11px] px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">Complet</span>
                                  : avail <= 1 ? <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">{avail} cab.</span>
                                  : <span className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{avail} cab.</span>}
                              </div>
                              {dep.cabin_price > 0 && (
                                <div className="flex-shrink-0 text-right w-[90px]">
                                  {discountPercent > 0 ? (
                                    <><div className="text-[10px] text-slate-400 line-through">{(dep.cabin_price * 2).toLocaleString('fr-FR')}€/cab</div>
                                    <div className="text-sm font-bold text-[#C4724F]">{(Math.round(dep.cabin_price * 2 * (1 - discountPercent/100))).toLocaleString('fr-FR')}€</div></>
                                  ) : <div className="text-sm font-semibold text-[#0A2540]">{(dep.cabin_price * 2).toLocaleString('fr-FR')}€/cab</div>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ===== STEP 2: PREMIUM PREVIEW ===== */
                  <div className="p-5 space-y-4" data-testid="email-preview">
                    <div className="flex items-center gap-2 text-sm text-slate-500"><Eye className="h-4 w-4" /> Aperçu exact de l'email que vos clients recevront :</div>

                    <div className="border rounded-2xl overflow-hidden shadow-lg bg-[#FAFAF7]">
                      {/* Header with logo on white band */}
                      <div className="bg-white p-4 text-center border-b border-slate-100">
                        <img src={LOGO_URL} alt="SOGNUDIMARE" className="h-[50px] mx-auto" />
                      </div>
                      <div className="bg-[#0A2540] px-5 py-2.5 text-center">
                        <div className="text-[#D4AF37]/70 text-[10px] tracking-[0.25em] uppercase font-semibold">Croisières catamaran d'exception · 2027</div>
                      </div>

                      {discountPercent > 0 && (
                        <div className="bg-[#C4724F] p-3.5 text-center">
                          <span className="text-white text-sm font-bold tracking-widest uppercase">Offre exceptionnelle · −{discountPercent}% sur votre croisière</span>
                        </div>
                      )}

                      <div className="relative">
                        <img src={LAGOON_HERO} alt="Catamaran Lagoon SOGNUDIMARE" className="w-full h-[220px] object-cover" />
                        <div className="absolute top-4 left-5">
                          <span className="inline-block px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 text-white text-[10px] font-bold tracking-widest uppercase">Corse · Sardaigne · Méditerranée</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/95 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <div className="font-display text-white text-2xl font-bold leading-tight">Prochains <span className="italic text-[#D4AF37]">départs</span> disponibles</div>
                          <div className="text-white/70 text-xs mt-1.5">Embarquez pour une aventure inoubliable, en pension complète avec équipage</div>
                        </div>
                      </div>

                      <div className="p-5 bg-[#FAFAF7] space-y-4">
                        {customMessage && (
                          <div className="bg-[#FFFBEB] border-l-4 border-[#D4AF37] p-4 rounded-r-lg">
                            <p className="text-slate-700 text-sm leading-relaxed italic">{customMessage}</p>
                          </div>
                        )}

                        {offerExpires && discountPercent > 0 && (
                          <div className="bg-[#0A2540] rounded-2xl p-5 text-center">
                            <div className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em]">Offre à durée limitée</div>
                            <div className="font-display text-white text-2xl font-bold mt-2">{fmtDateLong(offerExpires)}</div>
                            <div className="mt-3 flex items-center justify-center gap-3">
                              {countdown.split(' ').map((part, i) => (
                                <div key={i} className="bg-white/10 rounded-lg px-3 py-2 min-w-[56px]">
                                  <div className="text-white text-xl font-bold font-mono">{part.replace(/[a-z]/g, '')}</div>
                                  <div className="text-white/40 text-[9px] uppercase">{part.replace(/[0-9]/g, '')}</div>
                                </div>
                              ))}
                            </div>
                            <div className="text-[#D4AF37] text-xs mt-3 font-medium">Réservez avant cette date pour bénéficier de −{discountPercent}%</div>
                          </div>
                        )}

                        {selectedDepartures.map(dep => {
                          const photo = DEST_PHOTOS[dep.slug] || DEFAULT_DEST_PHOTO;
                          const av = dep.available_cabins ?? 0;
                          let badge;
                          if (av <= 0) badge = <span className="px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider">Complet</span>;
                          else if (av <= 1) badge = <span className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider">Dernière cabine</span>;
                          else if (av <= 2) badge = <span className="px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wider">{av} cabines restantes</span>;
                          else badge = <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">{av} cabines disponibles</span>;
                          return (
                            <div key={dep.id} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                              <div className="relative">
                                <img src={photo} alt={dep.destination} className="w-full h-[160px] object-cover" />
                                <div className="absolute top-3 left-3">
                                  <span className="inline-block bg-[#C4724F] text-white px-3 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase">{dep.destination}</span>
                                </div>
                              </div>
                              <div className="p-5">
                                <div className="font-display text-xl font-bold text-[#0A2540] leading-tight mb-1">{dep.cruise_name}</div>
                                <div className="text-slate-400 text-xs">{dep.duration} · Départ de {dep.departure_port}</div>
                                <div className="mt-3 flex items-center gap-2.5 flex-wrap">
                                  <div className="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl">
                                    <div className="text-slate-400 text-[9px] uppercase tracking-widest">Départ</div>
                                    <div className="text-[#0A2540] font-bold text-sm">{fmtDateLong(dep.start_date)}</div>
                                    <div className="text-slate-400 text-[10px]">au {fmtDateLong(dep.end_date)}</div>
                                  </div>
                                  {badge}
                                </div>
                                {dep.cabin_price > 0 && (
                                  <div className={`mt-3 p-3.5 rounded-xl ${discountPercent > 0 ? 'bg-[#FFF5F0] border border-[#FFE0CC]' : 'bg-[#F7F5F1]'}`}>
                                    {discountPercent > 0 ? (<>
                                      <div className="text-slate-400 text-[10px] line-through text-right">Cabine double : {(dep.cabin_price * 2).toLocaleString('fr-FR')} € — soit {dep.cabin_price.toLocaleString('fr-FR')} € / pers.</div>
                                      <div className="flex items-end justify-between mt-0.5">
                                        <div className="text-slate-400 text-[10px] uppercase tracking-widest">Tarif Anticipé</div>
                                        <div><span className="font-display text-[#C4724F] text-2xl font-bold">{(Math.round(dep.cabin_price * 2 * (1 - discountPercent/100))).toLocaleString('fr-FR')}€</span> <span className="text-[#C4724F] text-xs">/ cabine</span></div>
                                      </div>
                                      <div className="text-[#C4724F] text-[10px] text-right mt-0.5">soit {Math.round(dep.cabin_price * (1 - discountPercent/100)).toLocaleString('fr-FR')} € par passager</div>
                                      <div className="text-right mt-1.5"><span className="inline-block bg-[#C4724F] text-white px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider">−{discountPercent}%</span></div>
                                    </>) : (<>
                                      <div className="flex items-end justify-between">
                                        <div className="text-slate-400 text-[10px] uppercase tracking-widest">À partir de</div>
                                        <div><span className="font-display text-[#0A2540] text-2xl font-bold">{(dep.cabin_price * 2).toLocaleString('fr-FR')}€</span> <span className="text-slate-500 text-xs">/ cabine</span></div>
                                      </div>
                                      <div className="text-slate-500 text-[10px] text-right mt-0.5">soit {dep.cabin_price.toLocaleString('fr-FR')} € par passager</div>
                                    </>)}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Climate engagement block */}
                        <div className="rounded-2xl p-5 text-white" style={{background: 'linear-gradient(135deg, #5BA3B8 0%, #3E8AA5 50%, #1F6B85 100%)'}}>
                          <div className="text-[10px] font-bold tracking-widest uppercase opacity-90 mb-2">Engagement exclusif SOGNUDIMARE</div>
                          <div className="font-display text-lg font-bold leading-tight mb-2">Météo difficile ? <span className="italic text-[#FFE4B5]">Reportez sans frais</span> jusqu'à 12 mois.</div>
                          <div className="text-sm opacity-90 leading-relaxed">SOGNUDIMARE est l'un des rares croisiéristes à s'adapter activement aux changements climatiques. En cas de météo difficile annoncée, votre croisière peut être reportée — sans aucun frais.</div>
                        </div>

                        {/* 3 photos extérieur */}
                        <div>
                          <div className="text-sm font-bold text-[#0A2540] mb-2">Votre catamaran — LAGOON 46 / 43 / 38</div>
                          <div className="grid grid-cols-3 gap-1.5">
                            {PHOTOS_EXT.map((src, i) => <img key={i} src={src} alt={`Lagoon SOGNUDIMARE ext ${i+1}`} className="w-full h-[90px] object-cover rounded-lg" />)}
                          </div>
                        </div>

                        {/* 3 photos cabines/intérieur */}
                        <div>
                          <div className="text-sm font-bold text-[#0A2540] mb-2">Les cabines & intérieurs</div>
                          <div className="grid grid-cols-3 gap-1.5">
                            {PHOTOS_INT.map((src, i) => <img key={i} src={src} alt={`Cabine ${i+1}`} className="w-full h-[90px] object-cover rounded-lg" />)}
                          </div>
                          <div className="text-center text-slate-400 text-xs mt-2">Cabines doubles avec salles de bain privatives — Capitaine & Hôtesse/Cook</div>
                        </div>

                        {/* Inclus / Non inclus */}
                        {(allIncluded.length > 0 || allNotIncluded.length > 0) && (
                          <div className="bg-white rounded-2xl p-5 border border-slate-200">
                            <div className="font-display font-bold text-[#0A2540] mb-3">Ce que comprend votre croisière</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                              {allIncluded.length > 0 && (
                                <div>
                                  <div className="text-xs font-bold text-emerald-700 mb-2 uppercase tracking-wider">Inclus</div>
                                  {allIncluded.map((item, i) => <div key={i} className="flex items-start gap-2 text-sm text-slate-700 mb-1"><span className="text-[#2A9D8F] font-bold shrink-0">&#10003;</span> {item}</div>)}
                                </div>
                              )}
                              {allNotIncluded.length > 0 && (
                                <div>
                                  <div className="text-xs font-bold text-red-700 mb-2 uppercase tracking-wider">Non inclus</div>
                                  {allNotIncluded.map((item, i) => <div key={i} className="flex items-start gap-2 text-sm text-slate-500 mb-1"><span className="text-[#C4724F] font-bold shrink-0">&#10007;</span> {item}</div>)}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* 1% biodiversité signature */}
                        <div className="bg-white rounded-2xl p-4 border border-slate-200 flex items-center gap-3">
                          <div className="w-11 h-11 rounded-full bg-[#2A9D8F] text-white font-display font-bold flex items-center justify-center shrink-0">1%</div>
                          <div>
                            <div className="font-display font-bold text-[#0A2540] text-sm">1% reversé à la biodiversité corse</div>
                            <div className="text-slate-600 text-xs leading-snug">Chaque croisière soutient Mare Vivu (pollution plastique) et Tous Pour Chacun (inclusion).</div>
                          </div>
                        </div>

                        {/* 2 CTA: site + appeler capitaine */}
                        <div className="text-center space-y-3 py-3">
                          <div><span className="inline-block bg-[#0A2540] text-white px-9 py-3.5 rounded-full font-bold text-sm tracking-wide shadow-md">Découvrir nos croisières →</span></div>
                          <div><span className="inline-flex items-center gap-2 bg-white text-[#0A2540] px-7 py-2.5 rounded-full font-bold text-sm border-[1.5px] border-[#0A2540]"><Phone className="h-4 w-4" /> Appeler le capitaine · 07 62 45 74 42</span></div>
                        </div>

                        <div className="text-center text-slate-400 text-xs">contact@sognudimare-catamarans.com<br />www.sognudimare.com</div>
                      </div>

                      {/* Footer with logo */}
                      <div className="bg-white p-3 text-center border-t border-slate-100">
                        <img src={LOGO_URL} alt="SOGNUDIMARE" className="h-[28px] mx-auto mb-1" />
                      </div>
                      <div className="bg-[#0A2540] px-5 py-2.5 text-center">
                        <span className="text-[#D4AF37]/50 text-[10px] tracking-wider">Croisières catamaran · sognudimare.com</span>
                      </div>
                    </div>

                    {/* Recipients */}
                    <div className="bg-slate-50 rounded-lg p-3 mt-3">
                      <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Destinataires ({selectedClients.length})</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedClients.map(c => <span key={c.id} className="text-xs bg-white px-2.5 py-1 rounded-full border text-slate-700">{c.name} <span className="text-slate-400">({c.email})</span></span>)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t flex-shrink-0 flex justify-between items-center bg-slate-50 rounded-b-xl">
                {modalStep === 1 ? (
                  <>
                    <Button variant="outline" onClick={() => setShowSendModal(false)}>Annuler</Button>
                    <Button data-testid="go-to-preview-btn" onClick={goToPreview} disabled={selectedDepartureIds.size === 0} className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                      <Eye className="h-4 w-4 mr-2" /> Prévisualiser <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setModalStep(1)}><ArrowLeft className="h-4 w-4 mr-1" /> Modifier</Button>
                    <Button data-testid="confirm-send-btn" onClick={handleSendEmail} disabled={sending} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">
                      <Send className="h-4 w-4 mr-2" /> {sending ? 'Envoi...' : `Confirmer l'envoi à ${selectedClients.length} client(s)`}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminClients;
